"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BasicField = void 0;
/**
 * A field with a value, error, and touched state
 * @class
 * @internal
 */
class BasicField {
    /**
     * Creates a new field instance
     * @param name This field's name (unique value identifier, not label)
     * @param defaultValue Default value to use initially and on reset
     * @param onUpdate Callback to use after updates are made
     */
    constructor(name, defaultValue, onUpdate) {
        /** The current error message or null (no error) */
        this._errorOrNull = null;
        /** Whether this field has been touched */
        this._touched = false;
        this._name = name;
        this._defaultValue = defaultValue;
        this._value = defaultValue; // set initial value to default value
        this._onUpdate =
            onUpdate !== undefined
                ? onUpdate
                : () => {
                    // do nothing
                };
    }
    //////////////////////////////////////////////////////////////////////////////
    // getters
    //////////////////////////////////////////////////////////////////////////////
    /** This field's name */
    getName() {
        return this._name;
    }
    /** The current value */
    getValue() {
        return this._value;
    }
    /** Whether this field has an error */
    hasError() {
        return this._errorOrNull !== null;
    }
    /** The current error message or null (no error) */
    getErrorOrNull() {
        return this._errorOrNull;
    }
    /** Whether this field has been touched */
    isTouched() {
        return this._touched;
    }
    /** Whether this field is dirty (different from defaultValue) */
    isDirty() {
        return this._value !== this._defaultValue;
    }
    //////////////////////////////////////////////////////////////////////////////
    // setters
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Sets the current value and announces an update
     * @returns {boolean} Whether this field was updated
     */
    setValue(value) {
        if (this._value !== value) {
            this._value = value;
            this._onUpdate();
            return true;
        }
        return false;
    }
    /**
     * Sets the current error and announces an update
     * @returns {boolean} Whether this field was updated
     */
    setError(error) {
        if (this._errorOrNull !== error) {
            this._errorOrNull = error;
            this._onUpdate();
            return true;
        }
        return false;
    }
    /**
     * Clears the current error and announces an update
     * @returns {boolean} Whether this field was updated
     */
    clearError() {
        if (this._errorOrNull !== null) {
            this._errorOrNull = null;
            this._onUpdate();
            return true;
        }
        return false;
    }
    /**
     * Sets the touched state and announces an update
     * @returns {boolean} Whether this field was updated
     */
    setTouched(touched) {
        if (this._touched !== touched) {
            this._touched = touched;
            this._onUpdate();
            return true;
        }
        return false;
    }
    /** Resets the field and announces update */
    reset() {
        this._value = this._defaultValue;
        this._errorOrNull = null;
        this._touched = false;
        this._onUpdate();
    }
}
exports.BasicField = BasicField;
